import React from "react";
import {
  Compass,
  ClipboardList,
  Wrench,
  Rocket,
  LineChart,
} from "lucide-react";

const steps = [
  {
    n: "01",
    icon: Compass,
    title: "Discovery",
    desc: "We audit your business, ops, and growth bottlenecks.",
  },
  {
    n: "02",
    icon: ClipboardList,
    title: "Strategy",
    desc: "We design the systems and define measurable outcomes.",
  },
  {
    n: "03",
    icon: Wrench,
    title: "Build & Integrate",
    desc: "We engineer agents, funnels, automations and CRM hooks.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Launch",
    desc: "We deploy to production with monitoring and QA.",
  },
  {
    n: "05",
    icon: LineChart,
    title: "Optimize & Scale",
    desc: "We iterate on data, scale wins, and compound results.",
  },
];

export default function Process() {
  return (
    <section
      id="process"
      className="relative section-padding"
      data-testid="process-section"
    >
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
            <span className="w-6 h-px bg-[#39FF14]" />
            Process
            <span className="w-6 h-px bg-[#39FF14]" />
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Five steps to <span className="gradient-text-green">scale.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            A repeatable system for shipping production-grade growth
            infrastructure.
          </p>
        </div>

        {/* Desktop horizontal */}
        <div className="hidden lg:block relative">
          <div className="absolute top-7 left-0 right-0 h-px">
            <div className="h-px bg-gradient-to-r from-transparent via-[#39FF14]/30 to-transparent" />
          </div>

          <div className="grid grid-cols-5 gap-4 relative reveal">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className="group relative flex flex-col items-center text-center"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="process-node group-hover:active text-zinc-300">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="mt-5 text-[10px] font-mono tracking-widest text-zinc-500">
                  {s.n}
                </div>
                <div className="mt-1 text-lg font-semibold text-white">
                  {s.title}
                </div>
                <div className="mt-2 text-sm text-zinc-500 leading-relaxed px-2">
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden space-y-6 relative">
          <div className="absolute left-7 top-2 bottom-2 w-px bg-gradient-to-b from-[#39FF14]/40 via-[#39FF14]/10 to-transparent" />
          {steps.map((s, i) => (
            <div
              key={s.n}
              className="group flex gap-4"
              data-testid={`process-step-mobile-${i + 1}`}
            >
              <div className="process-node shrink-0 group-hover:active text-zinc-300">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-mono tracking-widest text-zinc-500">
                  {s.n}
                </div>
                <div className="mt-1 text-lg font-semibold text-white">
                  {s.title}
                </div>
                <div className="mt-1.5 text-sm text-zinc-500 leading-relaxed">
                  {s.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
